<script setup>
  import { reactive, ref, defineProps, defineEmits } from 'vue'
  import { ElMessage, ElMessageBox } from 'element-plus'
  import Clipboard from 'clipboard'
  import SubscriptionId from './SubscriptionId.vue'
  import { resetSubscriptionSecret, saveSubscription } from '@/api/registrationSubscription'

  const props = defineProps({
    meetingCode: { type: String, required: true },
    subscription: { type: Object, default: null },
  })
  const emit = defineEmits(['close', 'saved'])
  const formRef = ref(null)
  const saving = ref(false)
  const secret = ref('')
  async function resetSecret() {
    if (saving.value) return
    try {
      await ElMessageBox.confirm('重置后旧密钥立即失效，订阅将停用，未完成的推送将取消。确认重置？', '重置密钥', {
        type: 'warning',
        confirmButtonText: '确认重置',
        cancelButtonText: '取消',
      })
    } catch (error) {
      return
    }
    saving.value = true
    try {
      const { data } = await resetSubscriptionSecret(props.meetingCode, props.subscription.id)
      secret.value = data.secret
      form.enabled = false
      emit('saved')
    } finally {
      saving.value = false
    }
  }
  function copySecret(value, event) {
    const clipboard = new Clipboard(event.currentTarget, { text: () => value })
    clipboard.on('success', () => {
      ElMessage.success('密钥已复制')
      clipboard.destroy()
    })
    clipboard.on('error', () => {
      ElMessage.error('复制失败，请手动复制')
      clipboard.destroy()
    })
    clipboard.onClick(event)
  }
  const form = reactive({
    id: props.subscription?.id,
    receiverName: props.subscription?.receiverName || '',
    callbackUrl: props.subscription?.callbackUrl || '',
    statuses: props.subscription?.statuses?.split(',') || ['APPROVED', 'REJECTED', 'CANCELLED'],
    enabled: props.subscription?.requiresSecretReset ? false : props.subscription?.enabled || false,
  })
  const rules = {
    receiverName: [{ required: true, whitespace: true, message: '请输入接收方名称', trigger: 'blur' }],
    callbackUrl: [{ required: true, pattern: /^https:\/\/[^\s]+$/i, message: '请输入HTTPS回调地址', trigger: 'blur' }],
    statuses: [{ type: 'array', required: true, min: 1, message: '至少选择一种推送结果', trigger: 'change' }],
  }
  async function save() {
    if (saving.value) return
    saving.value = true
    try {
      await formRef.value.validate()
    } catch (error) {
      saving.value = false
      return
    }
    try {
      const { data } = await saveSubscription(props.meetingCode, { ...form, statuses: form.statuses.join(',') })
      form.id = data.id
      emit('saved')
      if (data.secret) secret.value = data.secret
      else {
        ElMessage.success('订阅已保存')
        emit('close')
      }
    } finally {
      saving.value = false
    }
  }
</script>

<template>
  <el-dialog
    :close-on-click-modal="false"
    :close-on-press-escape="!saving"
    :model-value="true"
    :show-close="!saving"
    :title="secret ? '共享密钥已生成' : subscription ? '编辑订阅' : '新增订阅'"
    width="680px"
    @close="emit('close')"
  >
    <div v-if="form.id != null" class="subscription-identity">
      <span>订阅ID：</span>
      <subscription-id :id="form.id" />
    </div>
    <template v-if="secret">
      <el-alert
        :closable="false"
        title="请保存新密钥，关闭后无法再次查看；更新接收方配置并测试后再启用订阅"
        type="warning"
      />
      <el-input aria-label="共享密钥" class="secret" :model-value="secret" readonly show-password type="password">
        <template #append><el-button @click="copySecret(secret, $event)">复制</el-button></template>
      </el-input>
    </template>
    <el-form v-else ref="formRef" :disabled="saving" label-width="110px" :model="form" :rules="rules">
      <el-form-item label="接收方名称" prop="receiverName">
        <el-input v-model="form.receiverName" maxlength="100" />
      </el-form-item>
      <el-form-item label="回调地址" prop="callbackUrl">
        <el-input v-model="form.callbackUrl" maxlength="2000" placeholder="https://" />
      </el-form-item>
      <el-form-item label="推送结果" prop="statuses">
        <el-checkbox-group v-model="form.statuses">
          <el-checkbox label="APPROVED">审核通过（含无需审核）</el-checkbox>
          <el-checkbox label="REJECTED">审核拒绝</el-checkbox>
          <el-checkbox label="CANCELLED">取消报名</el-checkbox>
        </el-checkbox-group>
      </el-form-item>
      <el-form-item label="启用状态"><el-switch v-model="form.enabled" /></el-form-item>
      <el-form-item label="共享密钥">
        <template v-if="subscription && !subscription.requiresSecretReset">
          <span>••••••••</span>
          <el-button :disabled="saving" type="text" @click="resetSecret">重置密钥</el-button>
        </template>
        <span v-else>保存后自动生成</span>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="saving" @click="emit('close')">{{ secret ? '关闭' : '取消' }}</el-button>
      <el-button v-if="!secret" :loading="saving" type="primary" @click="save">保存</el-button>
    </template>
  </el-dialog>
</template>

<style scoped>
  .subscription-identity {
    margin-bottom: 16px;
  }
  .secret {
    margin-top: 20px;
  }
</style>
